import { createRoute, useNavigate } from '@tanstack/react-router'
import { getLocalTimeZone, today } from '@internationalized/date'
import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import {
  CalendarClock,
  Grid3x3,
  Map as MapIcon,
  MapPin,
  Tag,
  X,
} from 'lucide-react'
import rootRoute from './rootRoute'
import type { components } from '@/lib/client'
import type { Event } from '@/lib/types'
import {
  Combobox,
  FilterPill,
  PageHeader,
  Row,
  Stack,
  Switch,
  errorToast,
  spacing,
} from '@/components/ui'
import EventCard from '@/components/EventCard'
import EventsMap from '@/components/EventsMap'
import LoadMore from '@/components/LoadMore'
import { catOf } from '@/lib/types'
import { LoginContext } from '@/lib/auth'
import { $api } from '@/lib/fetch'
import { EventStatus } from '@/lib/constants'
import Loader from '@/components/Loader'
import { searchCity } from '@/lib/geocode'

const RADIUS_OPTIONS: Array<number> = [5, 10, 25, 50, 100]

type View = 'split' | 'list'
type RangeKey = 'week' | 'month' | 'year'

const RANGE_OPTIONS: Array<{ value: RangeKey; label: string; emoji: string }> =
  [
    { value: 'week', label: 'Cette semaine', emoji: '📅' },
    { value: 'month', label: 'Ce mois-ci', emoji: '🗓️' },
    { value: 'year', label: 'Cette année', emoji: '📆' },
  ]

/** Upper bound (max_date) for a preset window, starting today. */
function rangeMaxDate(range: RangeKey): string {
  const t = today(getLocalTimeZone())
  const end =
    range === 'week'
      ? t.add({ weeks: 1 })
      : range === 'month'
        ? t.add({ months: 1 })
        : t.add({ years: 1 })
  return end.toString()
}

function EmptyState() {
  return (
    <div
      style={{
        textAlign: 'center',
        padding: 'var(--space-20) var(--gutter)',
        color: 'var(--ink-3)',
      }}
    >
      <img
        src="/assets/oumma-mark.svg"
        alt=""
        width="44"
        height="44"
        style={{ opacity: 0.4, marginBottom: 14 }}
      />
      <p style={{ fontSize: 17, color: 'var(--ink-2)' }}>
        Aucun événement ici pour l'instant — soyez le premier à rassembler les
        gens.
      </p>
    </div>
  )
}

function ViewToggle({
  view,
  setView,
}: {
  view: View
  setView: (v: View) => void
}) {
  const opt = (v: View, label: string, icon: React.ReactNode) => {
    const on = view === v
    return (
      <button
        onClick={() => setView(v)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          border: 'none',
          cursor: 'pointer',
          fontFamily: 'var(--font-body)',
          fontSize: 13,
          fontWeight: 600,
          padding: '6px 13px',
          borderRadius: 'var(--radius-pill)',
          background: on ? 'var(--surface-card)' : 'transparent',
          color: on ? 'var(--emerald-800)' : 'var(--ink-2)',
          boxShadow: on ? 'var(--shadow-xs)' : 'none',
        }}
      >
        {icon}
        {label}
      </button>
    )
  }
  return (
    <div
      style={{
        display: 'inline-flex',
        gap: 2,
        background: 'var(--surface-sunken)',
        borderRadius: 'var(--radius-pill)',
        padding: 3,
      }}
    >
      {opt('split', 'Carte', <MapIcon size={14} />)}
      {opt('list', 'Liste', <Grid3x3 size={14} />)}
    </div>
  )
}

type PaginationToken = components['schemas']['PaginationToken']

export interface NearFilter {
  lat: number
  lng: number
  km: number
  label: string
}

const DEFAULT_RADIUS_KM = 10

function encodeCity(label: string, lat: number, lng: number): string {
  return `${label}|${lat.toFixed(5)},${lng.toFixed(5)}`
}

function NearFilterRow({
  near,
  onPick,
  onClear,
}: {
  near?: NearFilter
  onPick: (next: NearFilter) => void
  onClear: () => void
}) {
  const currentValue = near ? encodeCity(near.label, near.lat, near.lng) : ''
  // Static options[] entry so the Combobox can resolve the current value back
  // to a display label (loadOptions only feeds the dropdown, not labelFor).
  const currentOption = near ? [{ label: near.label, value: currentValue }] : []

  const onCityChange = (value: string) => {
    if (!value) {
      onClear()
      return
    }
    const [label, coords] = value.split('|')
    if (!coords) return
    const [latStr, lngStr] = coords.split(',')
    const lat = parseFloat(latStr)
    const lng = parseFloat(lngStr)
    if (Number.isNaN(lat) || Number.isNaN(lng)) return
    onPick({ lat, lng, km: near?.km ?? DEFAULT_RADIUS_KM, label })
  }

  const pickRadius = (km: number) => {
    if (!near) return
    onPick({ ...near, km })
  }

  return (
    <Row wrap gap="sm" align="center">
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 13,
          fontWeight: 600,
          color: 'var(--ink-3)',
          marginRight: 2,
        }}
      >
        <MapPin size={15} /> Près de
      </span>
      <div style={{ minWidth: 220 }}>
        <Combobox
          value={currentValue}
          onChange={onCityChange}
          options={currentOption}
          loadOptions={searchCity}
          placeholder="Rechercher une ville…"
          minChars={3}
        />
      </div>
      {near &&
        RADIUS_OPTIONS.map((km) => (
          <FilterPill
            key={km}
            active={near.km === km}
            onClick={() => pickRadius(km)}
          >
            {km} km
          </FilterPill>
        ))}
      {near && (
        <FilterPill
          active={false}
          onClick={onClear}
          aria-label="Retirer le filtre de proximité"
        >
          <X size={14} /> Retirer
        </FilterPill>
      )}
    </Row>
  )
}

function EventsBrowser({
  events,
  isAdmin,
  includePast,
  setIncludePast,
  range,
  setRange,
  nextToken,
  onLoadMore,
  isFetching,
  near,
  setNear,
  types,
  setTypes,
}: {
  events: Array<Event>
  isAdmin: boolean
  includePast: boolean
  setIncludePast: (v: boolean) => void
  range: RangeKey
  setRange: (v: RangeKey) => void
  nextToken?: PaginationToken | null
  onLoadMore: () => void
  isFetching: boolean
  near?: NearFilter
  setNear: (next: NearFilter | null) => void
  types: Array<string>
  setTypes: (next: Array<string>) => void
}) {
  const [view, setView] = useState<View>('split')
  const [active, setActive] = useState<number | null>(null)

  const { data: typesData } = $api.useQuery('get', '/api/events/types')

  const availableTypes = useMemo<Array<[string, string]>>(
    () => (typesData?.types ?? []).map((t) => [t.type, t.emoji]),
    [typesData],
  )

  const selectedTypes = useMemo(() => new Set(types), [types])
  const list = events

  const toggleType = (t: string) => {
    const next = new Set(selectedTypes)
    next.has(t) ? next.delete(t) : next.add(t)
    setTypes(Array.from(next))
  }

  const gridCols =
    view === 'split'
      ? 'repeat(auto-fill, minmax(260px,1fr))'
      : 'repeat(auto-fill, minmax(300px,1fr))'

  return (
    <div>
      <div
        style={{
          background: 'var(--surface-raised)',
          borderBottom: '1px solid var(--border-soft)',
        }}
      >
        <div
          style={{
            maxWidth: 'var(--container-max)',
            margin: '0 auto',
            padding: 'var(--space-8) var(--gutter) var(--space-6)',
          }}
        >
          <PageHeader
            overline="Près de chez vous"
            title="Les événements de la communauté"
            style={{ marginBottom: spacing.lg }}
          />
          <Stack gap="lg">
            <Row wrap gap="sm">
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 13,
                  fontWeight: 600,
                  color: 'var(--ink-3)',
                  marginRight: 2,
                }}
              >
                <CalendarClock size={15} /> Période
              </span>
              {RANGE_OPTIONS.map((o) => (
                <FilterPill
                  key={o.value}
                  active={range === o.value}
                  onClick={() => setRange(o.value)}
                >
                  <span>{o.emoji}</span>
                  {o.label}
                </FilterPill>
              ))}
            </Row>
            <NearFilterRow
              near={near}
              onPick={setNear}
              onClear={() => setNear(null)}
            />
            {availableTypes.length > 0 && (
              <Row wrap gap="sm">
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    fontSize: 13,
                    fontWeight: 600,
                    color: 'var(--ink-3)',
                    marginRight: 2,
                  }}
                >
                  <Tag size={15} /> Types
                </span>
                {availableTypes.map(([t, emoji]) => {
                  const on = selectedTypes.has(t)
                  const cat = catOf(t)
                  return (
                    <FilterPill
                      key={t}
                      active={on}
                      activeColor={cat.color}
                      onClick={() => toggleType(t)}
                    >
                      <span>{emoji}</span>
                      {t}
                    </FilterPill>
                  )
                })}
              </Row>
            )}
            <Row
              justify="space-between"
              gap="md"
              wrap
              style={{ paddingTop: 4 }}
            >
              <div style={{ fontSize: 14.5, color: 'var(--ink-2)' }}>
                <strong
                  style={{
                    color: 'var(--ink-0)',
                    fontFamily: 'var(--font-mono)',
                  }}
                >
                  {list.length}
                  {nextToken ? '+' : ''}
                </strong>{' '}
                événement{list.length > 1 ? 's' : ''} trouvé
                {list.length > 1 ? 's' : ''}
              </div>
              <Row gap="lg" wrap>
                {isAdmin && (
                  <Switch
                    label="Événements passés"
                    checked={includePast}
                    onChange={(e) => setIncludePast(e.target.checked)}
                    style={{ fontSize: 13.5, color: 'var(--ink-2)' }}
                  />
                )}
                <ViewToggle view={view} setView={setView} />
              </Row>
            </Row>
          </Stack>
        </div>
      </div>

      {view === 'split' ? (
        <div
          className="oumma-events-split"
          style={{
            maxWidth: 1480,
            margin: '0 auto',
            display: 'grid',
            gridTemplateColumns: 'minmax(0,1.15fr) minmax(0,1fr)',
            alignItems: 'start',
          }}
        >
          <div style={{ padding: 'var(--space-8) clamp(20px,3vw,36px)' }}>
            {list.length ? (
              <>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: gridCols,
                    gap: 20,
                  }}
                >
                  {list.map((e) => (
                    <EventCard
                      key={e.id}
                      event={e}
                      compact
                      active={active === e.id}
                      onHover={setActive}
                    />
                  ))}
                </div>
                <LoadMore
                  onLoadMore={onLoadMore}
                  loading={isFetching}
                  hasMore={!!nextToken}
                />
              </>
            ) : (
              <EmptyState />
            )}
          </div>
          <div
            className="oumma-events-mapcol"
            style={{
              position: 'sticky',
              top: 60,
              height: 'calc(100vh - 60px)',
              padding: '16px 16px 16px 0',
            }}
          >
            <div
              style={{
                width: '100%',
                height: '100%',
                borderRadius: 'var(--radius-lg)',
                overflow: 'hidden',
                border: '1px solid var(--border-soft)',
                boxShadow: 'var(--shadow-sm)',
              }}
            >
              <EventsMap
                events={list}
                active={active}
                onActive={setActive}
                radius={
                  near
                    ? { lat: near.lat, lng: near.lng, km: near.km }
                    : undefined
                }
              />
            </div>
          </div>
        </div>
      ) : (
        <div
          style={{
            maxWidth: 'var(--container-max)',
            margin: '0 auto',
            padding: 'var(--space-10) var(--gutter) var(--space-20)',
          }}
        >
          {list.length ? (
            <>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: gridCols,
                  gap: 24,
                }}
              >
                {list.map((e) => (
                  <EventCard
                    key={e.id}
                    event={e}
                    active={active === e.id}
                    onHover={setActive}
                  />
                ))}
              </div>
              <LoadMore
                onLoadMore={onLoadMore}
                loading={isFetching}
                hasMore={!!nextToken}
              />
            </>
          ) : (
            <EmptyState />
          )}
        </div>
      )}
    </div>
  )
}

const EventsPage = () => {
  const ctx = useContext(LoginContext)
  const isAdmin = ctx?.user?.admin ?? false
  const search = eventsRoute.useSearch()
  const navigate = useNavigate({ from: eventsRoute.id })
  const [includePast, setIncludePast] = useState(false)
  const [range, setRange] = useState<RangeKey>('year')
  const [pageToken, setPageToken] = useState<PaginationToken | undefined>()
  const [accumulated, setAccumulated] = useState<Array<Event>>([])
  const isFirstPage = useRef(true)

  const minDate =
    includePast && isAdmin ? undefined : today(getLocalTimeZone()).toString()
  const maxDate = rangeMaxDate(range)

  const near: NearFilter | undefined =
    search.near_lat != null &&
    search.near_lng != null &&
    search.near_radius_km != null
      ? {
          lat: search.near_lat,
          lng: search.near_lng,
          km: search.near_radius_km,
          label: search.near_label ?? '',
        }
      : undefined

  const setNear = (next: NearFilter | null) => {
    navigate({
      search: (prev) => ({
        ...prev,
        near_lat: next?.lat,
        near_lng: next?.lng,
        near_radius_km: next?.km,
        near_label: next?.label,
      }),
      replace: true,
    })
  }

  const types = search.types ?? []
  const setTypes = (next: Array<string>) => {
    navigate({
      search: (prev) => ({ ...prev, types: next.length ? next : undefined }),
      replace: true,
    })
  }

  const distance =
    near != null
      ? { latitude: near.lat, longitude: near.lng, radius_km: near.km }
      : undefined

  // Only forward search-params the backend accepts; near_* are URL-only state.
  const { near_lat, near_lng, near_radius_km, near_label, ...apiSearch } =
    search

  // Reset pagination cursor when filters change. Leave `accumulated` in place
  // so the UI keeps showing prior results during the refetch (no Loader flash).
  const filterKey = JSON.stringify({ minDate, maxDate, apiSearch, distance })
  useEffect(() => {
    isFirstPage.current = true
    setPageToken(undefined)
  }, [filterKey])

  const { isError, error, isLoading, isFetching, data } = $api.useQuery(
    'post',
    '/api/events/search',
    {
      body: {
        ...apiSearch,
        min_date: minDate,
        max_date: maxDate,
        status: EventStatus.Verified,
        limit: 20,
        order: 'asc',
        page_token: pageToken,
        distance,
      },
    },
  )

  // Append new page to accumulation
  useEffect(() => {
    if (!data?.events) return
    if (isFirstPage.current) {
      setAccumulated(data.events)
      isFirstPage.current = false
    } else {
      setAccumulated((prev) => [...prev, ...(data.events ?? [])])
    }
  }, [data])

  if (isLoading && accumulated.length === 0) return <Loader />

  if (isError) {
    errorToast('Une erreur est survenue', error)
    return (
      <div style={{ padding: 'var(--space-20)', textAlign: 'center' }}>
        Une erreur est survenue.
      </div>
    )
  }

  return (
    <EventsBrowser
      events={accumulated}
      isAdmin={isAdmin}
      includePast={includePast}
      setIncludePast={setIncludePast}
      range={range}
      setRange={setRange}
      nextToken={data?.next_page_token}
      onLoadMore={() => setPageToken(data?.next_page_token)}
      isFetching={isFetching}
      near={near}
      setNear={setNear}
      types={types}
      setTypes={setTypes}
    />
  )
}

export const validateSearch = (
  search: any,
  _admin: boolean,
): Pick<
  components['schemas']['SearchEventsInputBody'],
  'max_date' | 'min_date' | 'search' | 'types'
> & {
  near_lat?: number
  near_lng?: number
  near_radius_km?: number
  near_label?: string
} => {
  const defaultMin = today(getLocalTimeZone()).toString()
  const km = Number(search.near_radius_km)
  const lat = Number(search.near_lat)
  const lng = Number(search.near_lng)
  const validKm = RADIUS_OPTIONS.includes(km) ? km : undefined
  return {
    max_date: search.max_date as string | undefined,
    min_date: (search.min_date as string | undefined) || defaultMin,
    search: search.search as string | undefined,
    types: search.types as Array<string>,
    near_lat: Number.isFinite(lat) ? lat : undefined,
    near_lng: Number.isFinite(lng) ? lng : undefined,
    near_radius_km: validKm,
    near_label: search.near_label as string | undefined,
  }
}

export const eventsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/events',
  validateSearch: (search) => validateSearch(search, false),
}).lazy(() => import('./EventsPage.lazy').then((m) => m.Route))

export default EventsPage
